import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type {
  JournalEntryDto,
  JournalEntryLineDto,
} from "@/features/ledger/api/dto";
import { JournalEntryRowActions } from "@/features/ledger/components/journal-entries/journal-entry-row-actions";
import {
  JOURNAL_ENTRY_STATUSES,
  type JournalEntryStatus,
} from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type JournalEntryRow = JournalEntryDto & { id: string };

const STATUS_VARIANTS: Record<
  JournalEntryStatus,
  "warning" | "success" | "destructive" | "secondary"
> = {
  PENDING: "warning",
  POSTED: "success",
  REJECTED: "destructive",
  REVERSED: "secondary",
};

/** Total des débits (unité mineure) d'une écriture — indicateur de montant en liste. */
export const journalEntryDebitTotalMinor = (
  lines?: JournalEntryLineDto[]
): number | null =>
  lines && lines.length > 0
    ? lines
        .filter((l) => l.direction === "DEBIT")
        .reduce((sum, l) => sum + l.amountMinor, 0)
    : null;

/**
 * Colonnes du journal comptable (`POST /JournalEntries/all`). Filtres :
 * `productCode`, `sourceService`, `sourceTransactionRef` (texte) et
 * `status` (select). Références et identifiants en `NoTranslate`.
 */
export const journalEntriesColumns: ColumnsClarFactory<JournalEntryRow> = (
  navigate
) => [
  {
    id: "reference",
    accessorKey: "reference",
    header: t("ledger.journal.columns.reference"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "productCode",
    accessorKey: "productCode",
    header: t("ledger.journal.columns.productCode"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "sourceService",
    accessorKey: "sourceService",
    header: t("ledger.journal.columns.sourceService"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "sourceTransactionRef",
    accessorKey: "sourceTransactionRef",
    header: t("ledger.journal.columns.sourceTransactionRef"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("ledger.journal.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as JournalEntryStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: JOURNAL_ENTRY_STATUSES.map((value) => ({ value, label: value })),
    },
  },
  {
    id: "currency",
    accessorKey: "currency",
    header: t("ledger.journal.columns.currency"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "businessDate",
    accessorKey: "businessDate",
    header: t("ledger.journal.columns.businessDate"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <JournalEntryRowActions entry={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
